import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { z } from 'zod';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { api } from '../../lib/api';
import { checkPasswordStrength, emailSchema, passwordSchema } from '../../lib/validation';

const agencyRegisterSchema = z
  .object({
    agencyName: z
      .string()
      .trim()
      .min(1, 'Agency name is required')
      .max(255, 'Agency name must be less than 255 characters'),
    email: emailSchema,
    password: passwordSchema,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type AgencyRegisterFormData = z.infer<typeof agencyRegisterSchema>;

export interface AgencyRegisterFormProps {
  onSuccess?: () => void;
}

export function AgencyRegisterForm({ onSuccess }: AgencyRegisterFormProps) {
  const WAITLIST_URL = 'https://mycarecal.com/waitlist';
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [hasConfirmedEligibility, setHasConfirmedEligibility] = useState(false);
  const [eligibilityError, setEligibilityError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isWaitlistDialogOpen, setIsWaitlistDialogOpen] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<AgencyRegisterFormData>({
    resolver: zodResolver(agencyRegisterSchema),
    mode: 'onBlur',
  });

  const password = watch('password', '');
  const passwordStrength = password ? checkPasswordStrength(password) : null;

  const handleEligibilityChange = (checked: boolean) => {
    setHasConfirmedEligibility(checked);
    if (checked) {
      setEligibilityError(null);
    }
  };

  const onSubmit = async (data: AgencyRegisterFormData) => {
    if (!hasConfirmedEligibility) {
      setEligibilityError('Please confirm that you are authorized to create this agency account.');
      return;
    }

    setIsSubmitting(true);

    try {
      await api.post('/auth/register', {
        email: data.email,
        password: data.password,
        userRole: 'SUPERVISOR',
        supervisorType: 'AGENCY',
        agencyName: data.agencyName.trim(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      toast.success('Account created! Please check your email to verify.');

      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/auth/verify-email', { state: { email: data.email } });
      }
    } catch (error) {
      if (
        typeof error === 'object' &&
        error !== null &&
        'statusCode' in error &&
        error.statusCode === 403
      ) {
        setIsWaitlistDialogOpen(true);
        return;
      }

      const errorMessage =
        error instanceof Error ? error.message : 'Registration failed. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label
            htmlFor="agencyName"
            className="block font-display text-sm font-semibold text-night-900 mb-2"
          >
            Agency Name
          </label>
          <Input
            id="agencyName"
            type="text"
            autoComplete="organization"
            placeholder="Your agency name"
            {...register('agencyName')}
            className={errors.agencyName ? 'border-error' : ''}
            disabled={isSubmitting}
          />
          {errors.agencyName && (
            <p className="mt-1 text-sm text-error" role="alert">
              {errors.agencyName.message}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="agencyEmail"
            className="block font-display text-sm font-semibold text-night-900 mb-2"
          >
            Email Address
          </label>
          <Input
            id="agencyEmail"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            {...register('email')}
            className={errors.email ? 'border-error' : ''}
            disabled={isSubmitting}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-error" role="alert">
              {errors.email.message}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="agencyPassword"
            className="block font-display text-sm font-semibold text-night-900 mb-2"
          >
            Password
          </label>
          <div className="relative">
            <Input
              id="agencyPassword"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Create a strong password"
              {...register('password')}
              className={errors.password ? 'border-error pr-10' : 'pr-10'}
              disabled={isSubmitting}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-night-300 hover:text-night-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blueviolet-light focus-visible:ring-offset-2 rounded"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-sm text-error" role="alert">
              {errors.password.message}
            </p>
          )}

          {password && passwordStrength && (
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-body text-night-500">Password Strength:</span>
                <span className="text-xs font-body font-medium text-night-700">
                  {passwordStrength.label}
                </span>
              </div>
              <div className="h-2 bg-night-100 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${passwordStrength.color}`}
                  style={{ width: `${(passwordStrength.score / 4) * 100}%` }}
                />
              </div>
              {passwordStrength.suggestions.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {passwordStrength.suggestions.map(suggestion => (
                    <li key={suggestion} className="text-xs font-body text-night-500">
                      - {suggestion}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div>
          <label
            htmlFor="agencyConfirmPassword"
            className="block font-display text-sm font-semibold text-night-900 mb-2"
          >
            Confirm Password
          </label>
          <div className="relative">
            <Input
              id="agencyConfirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Re-enter your password"
              {...register('confirmPassword')}
              className={errors.confirmPassword ? 'border-error pr-10' : 'pr-10'}
              disabled={isSubmitting}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-night-300 hover:text-night-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blueviolet-light focus-visible:ring-offset-2 rounded"
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="mt-1 text-sm text-error" role="alert">
              {errors.confirmPassword.message}
            </p>
          )}
        </div>

        <div>
          <div className="flex items-start gap-3">
            <Checkbox
              id="agency-eligibility-confirmation"
              checked={hasConfirmedEligibility}
              onCheckedChange={checked => handleEligibilityChange(checked === true)}
              disabled={isSubmitting}
              aria-describedby={eligibilityError ? 'agency-eligibility-error' : undefined}
            />
            <label
              htmlFor="agency-eligibility-confirmation"
              className="font-body text-sm leading-5 text-night-900"
            >
              I confirm I am authorized to create this agency account.
            </label>
          </div>
          {eligibilityError && (
            <p id="agency-eligibility-error" className="mt-2 text-sm text-error" role="alert">
              {eligibilityError}
            </p>
          )}
        </div>

        <Button type="submit" className="w-full" disabled={isSubmitting} size="lg">
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Creating Account...
            </>
          ) : (
            'Create Account'
          )}
        </Button>
      </form>

      <Dialog open={isWaitlistDialogOpen} onOpenChange={setIsWaitlistDialogOpen}>
        <DialogContent className="sm:max-w-md p-6 sm:p-7">
          <DialogHeader className="space-y-3">
            <DialogTitle>Registration is currently invite-only</DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-night-500">
              Join the waitlist and we will notify you when access opens.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-6 gap-3 sm:gap-2">
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => setIsWaitlistDialogOpen(false)}
            >
              Not now
            </Button>
            <Button
              className="w-full sm:w-auto"
              onClick={() => window.open(WAITLIST_URL, '_blank', 'noopener,noreferrer')}
            >
              Join Waitlist
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
